import combinedShotData from './data/combinedShotData.js';
import { useState } from 'react';
import Button from '@mui/material/Button';
import ShotCard from './ShotCard.js';
import applyFilters from './util/applyFilters.js';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Drawer from '@mui/material/Drawer';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import NBA_TEAM_LIST from './data/NBA_TEAM_LIST.js';
import { seasons, years, months, days, periods, minutes, seconds } from './data/timeframes.js';
import { useSearchParams } from "react-router-dom";
import Grid from '@mui/material/Grid';
  
function ShotList() {
  let [searchParams, setSearchParams] = useSearchParams();

  const pageLength = 50;
  const [numVisible, setNumVisible] = useState(pageLength);

  // Load more shots
  const loadMore = () => {
    setNumVisible(numVisible + pageLength);
  };

  // Toggle filter drawer
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer =
    (open) =>
    (event) => {
      if (
        event.type === 'keydown' &&
        (event.key === 'Tab' ||
          event.key === 'Shift')
      ) {
        return;
      }

      setDrawerOpen(open);
  };

  const getChangeHandler = (param) => (event) => {
    if(event.target.value === 'All') {
      searchParams.delete(param);
    } else {
      searchParams.set(param, event.target.value);
    }
    setSearchParams(searchParams);
  }

  const filterNames = {
    season: {
      name: 'Season'
    },
    opponent: {
      name: 'Opposing Team',
      shortName: 'Opponent'
    },
    threesInGame: {
      name: '3-Pointers made in the game',
      shortName: 'Threes in game',
      isNumber: true,
    },
    year: {
      name: 'Year',
      isNumber: true,
    },
    month: {
      name: 'Month',
      isNumber: true,
    },
    day: {
      name: 'Day',
      isNumber: true,
    },
    period: {
      name: 'PERIOD',
      shortName: 'Period',
      isNumber: true,
    },
    minutes: {
      name: 'MINUTES_REMAINING',
      shortName: 'Minutes left',
      isNumber: true,
    },
    seconds: {
      name: 'SECONDS_REMAINING',
      shortName: 'Seconds left',
      isNumber: true,
    },
  };

  const filterValues = {
    season: searchParams.get('season'),
    opponent: searchParams.get('opponent'),
    threesInGame: searchParams.get('threesInGame'),
    year: searchParams.get('year'),
    month: searchParams.get('month'),
    day: searchParams.get('day'),
    period: searchParams.get('period'),
    minutes: searchParams.get('minutes'),
    seconds: searchParams.get('seconds'),
  };

  const activeFilterList = Object.keys(filterValues).filter((filterName) => {
    return filterValues[filterName];
  }).map((filterName) => {
    return {
      key: filterName,
      value: filterValues[filterName],
    };
  });

  // Range filters
  const defaultDistanceMin = 22;
  const defaultDistanceMax = 60;

  let shotDistanceRange = null;
  let distanceMin = searchParams.get('distanceMin');
  let distanceMax = searchParams.get('distanceMax');
  // We want to use defaults to complete the range, but not change query param unless necessary
  if (distanceMin || distanceMax) {
    distanceMin = distanceMin || defaultDistanceMin;
    distanceMax = distanceMax || defaultDistanceMax;
    shotDistanceRange = [parseInt(distanceMin), parseInt(distanceMax)];
  }

  // We use state so the slider updates visually but doesn't navigate until committed
  const [tempDistanceMin, setTempDistanceMin] = useState(parseInt(distanceMin) || defaultDistanceMin);
  const [tempDistanceMax, setTempDistanceMax] = useState(parseInt(distanceMax) || defaultDistanceMax);

  const tempStateResetters = {
    distanceMin: () => setTempDistanceMin(defaultDistanceMin),
    distanceMax: () => setTempDistanceMax(defaultDistanceMax)
  };

  const handleShotDistanceRangeChange = (event, newRange) => {
    setTempDistanceMin(newRange[0]);
    setTempDistanceMax(newRange[1]);
  }
  const handleShotDistanceRangeChangeCommitted = (event, newRange) => {
    searchParams.set('distanceMin', newRange[0]);
    searchParams.set('distanceMax', newRange[1]);
    setSearchParams(searchParams);
  };

  const resetFilters = () => {
    activeFilterList.forEach((filterObj) => {
      searchParams.delete(filterObj.key);
    });
    searchParams.delete('distanceMin');
    searchParams.delete('distanceMax');

    tempStateResetters.distanceMin();
    tempStateResetters.distanceMax();

    setSearchParams(searchParams);
  };

  const getDeleteHandler = (filterKey) => {
    return () => {
      searchParams.delete(filterKey);
      setSearchParams(searchParams);

      // if distance min or distanceMax, update temp values too
      if(tempStateResetters[filterKey]) {
        tempStateResetters[filterKey]();
      }
    }
  };

  // Apply filters
  const filterConditions = [];

  activeFilterList.forEach((filterObj) => {
    const filterNameObj = filterNames[filterObj.key];
    const filterValue = filterValues[filterObj.key];
    filterConditions.push({
      category: filterNameObj.name,
      targetValue: filterNameObj.isNumber ? parseInt(filterValue) : filterValue
    });
  });

  if(shotDistanceRange) {
    filterConditions.push({
      category: 'SHOT_DISTANCE',
      range: shotDistanceRange
    });  
  }

  const filteredShotList = applyFilters(combinedShotData, filterConditions);

  return (
    <div className="shot-list">
      <h2>Explore the 3-pointers</h2>
      <Button variant="outlined" onClick={toggleDrawer(true)}>Filter</Button>
      <Drawer
        anchor="bottom"
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
      >
        <div className="filter-screen">
          <div className="filter-screen-header">
            <h3>Filter by</h3>
            <div className="filter-screen_ctas">
              <Button variant="text" onClick={resetFilters}>Reset</Button>
              <IconButton aria-label="Close filters" onClick={toggleDrawer(false)}>
                <CloseIcon/>
              </IconButton>
            </div>
            
          </div>
          <div className="filter-screen_form-element">
            <FormControl fullWidth>
              <InputLabel id="season-select-label">Season</InputLabel>
              <Select
                labelId="season-select-label"
                id="season-select"
                value={filterValues.season || 'All'}
                label="Season"
                onChange={getChangeHandler('season')}
              >
                <MenuItem value={'All'}>All</MenuItem>
                {
                  seasons.map((season) => {
                    return (
                      <MenuItem key={season} value={season}>{season}</MenuItem>      
                    )
                  })
                }
              </Select>
            </FormControl>
          </div>
          <div className="filter-screen_form-element">
            <FormControl fullWidth>
              <InputLabel id="opposing-team-select-label">Opposing Team</InputLabel>
              <Select
                labelId="opposing-team-select-label"
                id="opposing-team-select"
                value={filterValues.opponent || 'All'}
                label="Opposing Team"
                onChange={getChangeHandler('opponent')}
              >
                <MenuItem value={'All'}>All</MenuItem>
                {
                  NBA_TEAM_LIST.map((team) => {
                    return (
                      <MenuItem key={team.abbreviation} value={team.abbreviation}>{team.teamName}</MenuItem>      
                    )
                  })
                }
              </Select>
            </FormControl>
          </div>
          <div className="filter-screen_form-element">
            <FormControl fullWidth>
              <InputLabel id="threes-made-select-label">3-Pointers made in the game</InputLabel>
              <Select
                labelId="threes-made-select-label"
                id="threes-made-select"
                value={filterValues.threesInGame || 'All'}
                label="3-Pointers made in the game"
                onChange={getChangeHandler('threesInGame')}
              >
                <MenuItem value={'All'}>All</MenuItem>
                {
                  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map((val) => {
                    return (
                      <MenuItem key={val} value={val}>{val}</MenuItem>      
                    )
                  })
                }
              </Select>
            </FormControl>
          </div>
          <div className="filter-screen_form-element">
            <Typography id="shot-distance-slider" gutterBottom>
              Shot distance
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                {tempDistanceMin ? tempDistanceMin : defaultDistanceMin}
              </Grid>
              <Grid item xs>
                <Slider
                  getAriaLabel={() => 'Shot distance range'}
                  value={[tempDistanceMin, tempDistanceMax]}
                  onChange={handleShotDistanceRangeChange}
                  onChangeCommitted={handleShotDistanceRangeChangeCommitted}
                  valueLabelDisplay="auto"
                  aria-labelledby="shot-distance-slider"
                  min={defaultDistanceMin}
                  max={defaultDistanceMax}
                />
              </Grid>
              <Grid item>
                {tempDistanceMax ? tempDistanceMax : defaultDistanceMax}
              </Grid>
            </Grid>
          </div>
          <div className="filter-screen_form-element">
            <Typography gutterBottom>
              Date
            </Typography>

            <div className="filter-screen_date-row">
              <FormControl className="filter-screen-date-control">
                <InputLabel id="year-select-label">Year</InputLabel>
                <Select
                  labelId="year-select-label"
                  id="year-select"
                  value={filterValues.year || 'All'}
                  label="Year"
                  onChange={getChangeHandler('year')}
                >
                  <MenuItem value={'All'}>All</MenuItem>
                  {
                    years.map((year) => {
                      return (
                        <MenuItem key={year} value={year}>{year}</MenuItem>      
                      )
                    })
                  }
                </Select>
              </FormControl>
              <FormControl className="filter-screen-date-control">
                <InputLabel id="month-select-label">Month</InputLabel>
                <Select
                  labelId="month-select-label"
                  id="month-select"
                  value={filterValues.month || 'All'}
                  label="Month"
                  onChange={getChangeHandler('month')}
                >
                  <MenuItem value={'All'}>All</MenuItem>
                  {
                    months.map((month) => {
                      return (
                        <MenuItem key={month} value={month}>{month}</MenuItem>      
                      )
                    })
                  }
                </Select>
              </FormControl>
              <FormControl className="filter-screen-date-control">
                <InputLabel id="day-select-label">Day</InputLabel>
                <Select
                  labelId="day-select-label"
                  id="day-select"
                  value={filterValues.day || 'All'}
                  label="Day"
                  onChange={getChangeHandler('day')}
                >
                  <MenuItem value={'All'}>All</MenuItem>
                  {
                    days.map((day) => {
                      return (
                        <MenuItem key={day} value={day}>{day}</MenuItem>      
                      )
                    })
                  }
                </Select>
              </FormControl>
            </div>
          </div>
          <div className="filter-screen_form-element">
            <Typography gutterBottom>
              Time
            </Typography>

            <div className="filter-screen_date-row">
              <FormControl className="filter-screen-date-control">
                <InputLabel id="period-select-label">Period</InputLabel>
                <Select
                  labelId="period-select-label"
                  id="period-select"
                  value={filterValues.period || 'All'}
                  label="Period"
                  onChange={getChangeHandler('period')}
                >
                  <MenuItem value={'All'}>All</MenuItem>
                  {
                    periods.map((period) => {
                      return (
                        <MenuItem key={period} value={period}>{period}</MenuItem>      
                      )
                    })
                  }
                </Select>
              </FormControl>
              <FormControl className="filter-screen-date-control">
                <InputLabel id="minutes-select-label">Minutes</InputLabel>
                <Select
                  labelId="minutes-select-label"
                  id="minutes-select"
                  value={filterValues.minutes || 'All'}
                  label="Minutes"
                  onChange={getChangeHandler('minutes')}
                >
                  <MenuItem value={'All'}>All</MenuItem>
                  {
                    minutes.map((minutes) => {
                      return (
                        <MenuItem key={minutes} value={minutes}>{minutes}</MenuItem>      
                      )
                    })
                  }
                </Select>
              </FormControl>
              <FormControl className="filter-screen-date-control">
                <InputLabel id="seconds-select-label">Seconds</InputLabel>
                <Select
                  labelId="seconds-select-label"
                  id="seconds-select"
                  value={filterValues.seconds || 'All'}
                  label="Seconds"
                  onChange={getChangeHandler('seconds')}
                >
                  <MenuItem value={'All'}>All</MenuItem>
                  {
                    seconds.map((seconds) => {
                      return (
                        <MenuItem key={seconds} value={seconds}>{seconds}</MenuItem>      
                      )
                    })
                  }
                </Select>
              </FormControl>
            </div>
          </div>
        </div>
      </Drawer>
      <Stack className="filter-chip-stack" direction="row" spacing={1} flexWrap="wrap">
        {activeFilterList.map((filterObj) => {
          return (
            <Chip
              key={filterObj.key}
              label={`${filterNames[filterObj.key].shortName || filterNames[filterObj.key].name}: ${filterObj.value}`}
              onDelete={getDeleteHandler(filterObj.key)}
            />
          );
        })}
        {
          shotDistanceRange && shotDistanceRange[0] && shotDistanceRange[0] !== defaultDistanceMin && (
            <Chip
              label={`Min distance: ${shotDistanceRange[0]}`}
              onDelete={getDeleteHandler('distanceMin')}
            />
          )
        }
        {
          shotDistanceRange && shotDistanceRange[1] && shotDistanceRange[1] !== defaultDistanceMax && (
            <Chip
              label={`Max distance: ${shotDistanceRange[1]}`}
              onDelete={getDeleteHandler('distanceMax')}
            />
          )
        }
      </Stack>
      <p>Showing: {Math.min(numVisible, filteredShotList.length)} of {filteredShotList.length}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
        {filteredShotList.slice(0, numVisible).map((item) => (
          <div key={item["3-Pointer Number"]} style={{padding: 10}}>
            <ShotCard shotInfo={item}/>
          </div>
        ))}
      </div>
      {
        numVisible < filteredShotList.length && (
          <Button variant="outlined" onClick={loadMore}>Load more</Button>
        )
      }
      
    </div>)
}

export default ShotList;